import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { catchError } from 'rxjs/operators';

import { User, UserManager, UserManagerSettings } from 'oidc-client';
import { ConsoleService } from '../console.service';
import { AuthTokenService } from './auth-token.service';
import { BaseService } from './base.service';
import { ClientDataInitializerService } from './client-data-initializer.service';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseService {
  #authNavStatusSource = signal<boolean>(false);

  private manager = new UserManager(this.getClientSettings());
  private user: User | null;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private clientDataService: ClientDataInitializerService
  ) {
    super();
    this.setUser();
  }

  setUser() {
    this.manager.getUser().then((user) => {
      this.user = user;
      this.#authNavStatusSource.set(this.isAuthenticated());

      if (this.isAuthenticated()) this.setAuthToken();
    });
  }

  login() {
    return this.manager.signinRedirect();
  }

  async completeAuthentication() {
    this.user = await this.manager.signinRedirectCallback();
    this.setAuthToken();
    this.#authNavStatusSource.set(this.isAuthenticated());
  }

  register(userRegistration: any) {
    return this.http
      .post(this.configService.authApiURI + '/account', userRegistration)
      .pipe(catchError(this.handleError));
  }

  isAuthenticated(): boolean {
    return this.user != null && !this.user.expired;
  }

  setAuthToken() {
    AuthTokenService.setAccessToken(this.user.id_token);
  }

  get authorizationHeaderValue(): string {
    return `${this.user.token_type} ${this.user.access_token}`;
  }

  get name(): string {
    return this.user != null ? this.user.profile.name : '';
  }

  get userId(): string {
    return this.user != null ? this.user.profile.sub : '';
  }

  getUserData(): UserInfo {
    if (!this.user) return;

    return {
      userName: this.user.profile?.name,
      authTime: this.user.profile?.auth_time,
      email: this.user.profile?.email,
      birthDate: this.user.profile?.birthdate,
      gender: this.user.profile?.gender,
      ip: this.user.profile?.idp,
    };
  }

  async signout() {
    await this.manager.signoutRedirect();
  }

  getClientSettings(): UserManagerSettings {
    try {
      if (!this.isAuthenticated) AuthTokenService.clearAccessToken();
      const ClientData = this.getCurrentClientData();
      return {
        // authority: 'https://identityserver.pishgamrayan.com',
        // client_id: 'pishgamrayan.identity.testclient',
        // client_secret: '1qazxsw2',
        authority: ClientData.identityDomain,
        client_id: ClientData.clientId,
        client_secret: ClientData.clientSecret,
        redirect_uri: getCurrentDomain() + '/auth-callback',
        post_logout_redirect_uri: getCurrentDomain() + '/',
        response_type: 'id_token token',
        scope: 'openid profile website name id_token',
        filterProtocolClaims: true,
        loadUserInfo: true,
        automaticSilentRenew: false,
        // silent_redirect_uri: 'http://localhost:4200/silent-refresh.html'
        // silent_redirect_uri: 'https://smspanel.pishgamrayan.com/'
      };
    } catch (error) {
      ConsoleService.error(
        'Error (AuthService -> getClientSettings) =>',
        error
      );
    }
  }

  getCurrentClientData(): any {
    return this.clientDataService.getCurrentClientData;
  }
}

export function getCurrentDomain(): string {
  return window.location.origin;
}

export class UserInfo {
  userName?: string;
  email?: string;
  authTime?: number | string;
  ip?: string;
  gender?: string;
  birthDate?: string;
}
